import { Box, Breadcrumb, Button, Flex, Notification, StatusLabel, Tag } from '@cowsquare/design';
import { Link, navigate } from 'gatsby';
import * as R from 'ramda';
import React, { useState } from 'react';
import { graphql } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { GET_CME_EVENTS_RECORD } from '../../../../apollo/graphql/events';
import { GET_HKDU_USER_INFO, GET_USER_INFO } from '../../../../apollo/graphql/user';
import { chargeForEvent } from '../../../../utils/chargeForEvent';
import { dateFormat, getDuration } from '../../../../utils/dateFormat';
import {
  getDoctorId,
  getEnrollButtonText,
  isEventEnrolled,
  isEventExpired,
  isSkipEventsQuery,
} from '../../../../utils/doctorUtils';
import { updateEventQuery } from '../../../../utils/updateEventQuery';
import { getEventPrice, getEventPriceStr } from '../../../../utils/eventPrice';
import { priceByRole } from '../../../../utils/eventPrice';
import { BreadcrumbWrapper } from '../../../BreadcrumbWrapper';
import { HTMLContent } from '../../../HTMLContent';
import { Field, RowLink, RowTitle } from '../../../MedicalContent';
import { Section } from '../../../Section';
import { Title } from '../../../Title';
import PageNotFound from '../../error/404';

const EnrollButton = styled(Button).attrs({
  size: 'large',
  variant: 'primary',
})`
  width: 150px;
`;

const EventContent = ({ eventData, HKDUUserInfo, id, userInfo }) => {
  const [isEnrolling, setIsEnrolling] = useState(false);

  if (!R.prop('loading', eventData) && !R.prop('record', eventData)) {
    return <PageNotFound />;
  }

  const record = R.propOr({}, 'record', eventData);
  const title = R.propOr('', 'name', record);
  const content = R.propOr('', 'content', record);
  const tags = R.pipe(R.propOr([], 'tags'), R.map(R.path(['tag', 'name'])))(record);
  const doctorId = getDoctorId(HKDUUserInfo);
  const enrolled = isEventEnrolled(record);
  const expired = isEventExpired(record);
  const role = R.pathOr('', ['user', 'role'], userInfo);

  return (
    <Section>
      <Helmet>
        <title>{title} - HKDU CME</title>
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Item linkProps={{ as: Link, to: '/cme/events' }}>CME Events</Breadcrumb.Item>
          <Breadcrumb.Item>{title}</Breadcrumb.Item>
        </Breadcrumb>
      </BreadcrumbWrapper>
      <Box px={[5, 0]} py={8}>
        {enrolled && (
          <Box mb={5}>
            <StatusLabel type="success">Enrolled</StatusLabel>
          </Box>
        )}
        <Title mb={4}>{title}</Title>
        <Tag.Group mt={5} mb={6} itemStyle={{ mr: '5px' }}>
          {R.map(
            tag => (
              <Tag
                key={tag}
                mr={3}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  navigate(`/cme/events?tag=${tag}`);
                }}
              >
                {tag}
              </Tag>
            ),
            tags
          )}
        </Tag.Group>
        <Box mb={8}>
          <RowTitle>Event Information</RowTitle>
          <Field label="Date" value={record.date} render={value => dateFormat(value)} />
          <Field
            label="Time"
            value={getDuration(record)}
            render={value => `${value.startTime}-${value.endTime}`}
          />
          <Field label="Speaker" value={record.speaker} />
          <Field label="Location" value={record.location} />
          <Field label="CME Point" value={record.score} />
          <Field label="Price" value={getEventPrice(record)} render={getEventPriceStr} />
          <Field
            label="Organizer"
            value={record.organizer}
            render={value => (
              <RowLink as={Link} to={`/cme/event-organizers/${value.id}`}>
                {value.name}
              </RowLink>
            )}
          />
        </Box>
        <HTMLContent html={content} />
      </Box>
      {!record.unenrollable && (
        <Box mb={8}>
          <Flex justifyContent="center">
            <EnrollButton
              disabled={enrolled || expired || isEnrolling}
              onClick={() => {
                if (priceByRole(record)(role) === 0) {
                  setIsEnrolling(true);
                  return chargeForEvent(doctorId, id, 'cme_events')
                    .then(result => {
                      Notification.success({
                        message: 'Success!',
                        description: R.pathOr('', ['data', 'message'])(result),
                      });
                    })
                    .catch(err => {
                      setIsEnrolling(false);
                      Notification.error({
                        message: 'Failed!',
                        description: R.pathOr('', ['response', 'data', 'message'])(err),
                      });
                    })
                    .then(updateEventQuery('cme_events', id, doctorId));
                } else {
                  navigate(`/cme/events/checkout/${id}`);
                }
              }}
            >
              {getEnrollButtonText(record)}
            </EnrollButton>
          </Flex>
        </Box>
      )}
    </Section>
  );
};

export default R.compose(
  graphql(GET_USER_INFO, { name: 'userInfo' }),
  graphql(GET_HKDU_USER_INFO, {
    name: 'HKDUUserInfo',
    options: ({ userInfo }) => ({
      variables: {
        id: R.pathOr(null, ['user', 'id'], userInfo),
      },
    }),
  }),
  graphql(GET_CME_EVENTS_RECORD, {
    name: 'eventData',
    fetchPolicy: 'network-only',
    skip: isSkipEventsQuery,
    options: ({ id, HKDUUserInfo }) => ({
      variables: {
        id,
        doctor_id: getDoctorId(HKDUUserInfo),
      },
    }),
  })
)(EventContent);
